
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  ClipboardList,
  Users,
  Briefcase,
  User,
  Settings,
  TrendingUp,
  Bot,
  BrainCircuit,
  FileText,
  FileCheck,
  Bell,
  ShieldCheck,
  UserPlus,
  MessageSquare,
  BookOpen,
  X,
} from "lucide-react";

const Sidebar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 768px)").matches
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const updateScreen = () => {
      setIsMobile(media.matches);
      if (!media.matches) setMobileOpen(false);
    };

    media.addEventListener("change", updateScreen);
    return () => media.removeEventListener("change", updateScreen);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const toggle = () => setMobileOpen((open) => !open);
    const close = () => setMobileOpen(false);

    window.addEventListener("smart-hr:toggle-sidebar", toggle);
    window.addEventListener("smart-hr:close-sidebar", close);

    return () => {
      window.removeEventListener("smart-hr:toggle-sidebar", toggle);
      window.removeEventListener("smart-hr:close-sidebar", close);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  if (!user) return null;

  const role = String(user.role || "").toLowerCase();

  const employeeMenu = [
    { id: "employee-dashboard", path: "/employee/dashboard", label: "Vue globale", icon: LayoutDashboard },
    { id: "employee-leave-create", path: "/employee/leave-create", label: "Demander congé", icon: Calendar },
    { id: "employee-leave-history", path: "/employee/leave-history", label: "Mes demandes", icon: ClipboardList },
    { id: "employee-space", path: "/employee/dashboard", label: "Mon espace Employé", icon: User, isSwitch: true },
  ];

  const managerMenu = [
    { id: "manager-dashboard", path: "/manager/dashboard", label: "Suivi équipe", icon: LayoutDashboard },
    { id: "manager-leaves", path: "/manager/leaves", label: "Demandes à valider", icon: ClipboardList },
    { id: "manager-employee-space", path: "/employee/dashboard", label: "Mon espace Employé", icon: User, isSwitch: true },
  ];

  const rhMenu = [
    { id: "rh-dashboard", path: "/rh/dashboard", label: "Stats globales", icon: LayoutDashboard },
    { id: "rh-employees", path: "/rh/employees", label: "Collaborateurs", icon: Users },
    { id: "rh-calendar", path: "/rh/calendar", label: "Calendrier RH", icon: Calendar },
    { id: "rh-holidays", path: "/rh/holidays", label: "Jours fériés", icon: CalendarDays },
    { id: "rh-leaves", path: "/rh/leaves", label: "Validations RH", icon: ClipboardList },
    { id: "rh-leaves-history", path: "/rh/leaves/history", label: "Historique Congés", icon: FileText },
    { id: "rh-articles", path: "/rh/articles", label: "Articles", icon: BookOpen },
    { id: "rh-contracts", path: "/rh/contracts", label: "Contrats", icon: FileCheck },
    { id: "rh-notifications", path: "/rh/notifications", label: "Notifications", icon: Bell },
    { id: "rh-audit", path: "/rh/audit", label: "Journal d'audit", icon: ShieldCheck },
    { id: "rh-job-offers", path: "/rh/job-offers", label: "Offres d'emploi", icon: Briefcase },
    { id: "rh-applications", path: "/rh/applications", label: "Candidatures", icon: UserPlus },
    { id: "rh-interview-preparation", path: "/rh/interview-preparation", label: "Préparation entretiens", icon: MessageSquare },
    { id: "rh-ai-dashboard", path: "/rh/ai-dashboard", label: "Recrutement IA", icon: BrainCircuit },
    { id: "rh-cv-ranking", path: "/rh/cv-ranking", label: "Ranking Détaillé", icon: TrendingUp },
    { id: "rh-assistant", path: "/rh/assistant", label: "Assistant IA", icon: Bot },
    { id: "rh-employee-space", path: "/employee/dashboard", label: "Mon espace Employé", icon: User, isSwitch: true },
  ];

  let menuItems = [];
  if (role === "employee") {
    menuItems = [...employeeMenu];

    if (user.role === "rh") {
      menuItems.push({
        id: "employee-return-rh",
        path: "/rh/dashboard",
        label: "Retour espace RH",
        icon: LayoutDashboard,
        isSwitch: true,
      });
    }

    if (user.role === "manager") {
      menuItems.push({
        id: "employee-return-manager",
        path: "/manager/dashboard",
        label: "Retour espace Manager",
        icon: LayoutDashboard,
        isSwitch: true,
      });
    }
  } else if (role === "manager") {
    menuItems = managerMenu;
  } else if (role === "rh" || role === "admin") {
    menuItems = rhMenu;
  }

  const isRouteActive = (path) =>
    location.pathname === path ||
    (path !== "/rh/dashboard" && location.pathname.startsWith(`${path}/`));

  const profileRoutes = {
    rh: "/rh/profile",
    admin: "/rh/profile",
    employee: "/employee/profile",
    manager: "/manager/profile",
  };

  const navigateTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebarStyle = {
    position: isMobile ? "fixed" : "relative",
    top: isMobile ? 0 : undefined,
    bottom: isMobile ? 0 : undefined,
    left: isMobile ? 0 : undefined,
    zIndex: isMobile ? 1200 : "auto",
    width: isMobile ? "min(84vw, 320px)" : 260,
    maxWidth: "100%",
    minWidth: 0,
    height: isMobile ? "100dvh" : "auto",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    padding: isMobile ? "16px 12px" : undefined,
    background: "var(--bg-main)",
    boxShadow: isMobile && mobileOpen
      ? "8px 0 30px rgba(0,0,0,0.16)"
      : undefined,
    transform: isMobile
      ? mobileOpen
        ? "translateX(0)"
        : "translateX(-105%)"
      : "none",
    visibility: isMobile && !mobileOpen ? "hidden" : "visible",
    transition: "transform 0.25s ease, visibility 0.25s ease",
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1199,
            background: "rgba(0,0,0,0.42)",
          }}
        />
      )}

      <aside
        className={`sidebar-global ${mobileOpen ? "sidebar-mobile-open" : ""}`}
        style={sidebarStyle}
        aria-label="Navigation principale"
        aria-hidden={isMobile && !mobileOpen}
      >
        {isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "4px 4px 16px",
              marginBottom: 12,
              borderBottom: "1px solid var(--border)",
            }}
          >
            <strong>Menu Smart HR</strong>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Fermer le menu"
              style={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                border: "1px solid var(--border)",
                borderRadius: 10,
                background: "transparent",
                color: "var(--text-main)",
              }}
            >
              <X size={22} />
            </button>
          </div>
        )}

        <div className="sidebar-info">
          <div className="sidebar-dept-badge">
            {user.department || "Département"}
          </div>
        </div>

        <ul
          className="sidebar-menu"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            flex: 1,
          }}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isRouteActive(item.path);

            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => navigateTo(item.path)}
                  className={[
                    "sidebar-link",
                    active ? "active" : "",
                    item.isSwitch ? "workspace-switch" : "",
                  ].filter(Boolean).join(" ")}
                  aria-current={active ? "page" : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    minHeight: 46,
                    padding: "10px 12px",
                    boxSizing: "border-box",
                    textAlign: "left",
                    cursor: "pointer",
                    borderRadius: 8,
                  }}
                >
                  <Icon size={19} className="sidebar-icon" />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="sidebar-footer">
          <button
            type="button"
            className={`sidebar-link ${
              location.pathname.endsWith("/profile") ? "active" : ""
            }`}
            onClick={() => navigateTo(profileRoutes[role] || "/profile")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              minHeight: 46,
              padding: "10px 12px",
              boxSizing: "border-box",
              textAlign: "left",
              cursor: "pointer",
              borderRadius: 8,
            }}
          >
            <Settings size={19} className="sidebar-icon" />
            <span>Profil</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
